/**
 * Versioned save format. Bump SCHEMA_VERSION on any shape change and add a
 * step to migrate(). Saves are JSON strings; the 1 MB guard protects portal
 * storage quotas (CrazyGames data module is localStorage-shaped).
 *
 * Since Increment L1.1 the save carries the run in progress. `RunState` is plain JSON by
 * core's own rule (no clocks, no classes, PRNG state inside), so it is stored as it is. A
 * run resumes only under the `CORE_VERSION` that wrote it and the seed text it was started
 * from: an eight-minute run is not worth a migration, so a run from an older core is dropped
 * and the unlocks stay.
 *
 * Since ADR-0014 slice M2 the save carries the account's purse: what the human's soldier has
 * earned over every run and kept. Since ADR-0015 slice C1 it carries renown beside the purse.
 * What a King owes the soldier is the run's and is never banked.
 *
 * Since K3 of the seat-screens sketch the run carries what it started with (`RunStart`), so
 * the end screen of a resumed run still says what the run earned. A run saved by v4 comes over
 * without it and its end screen names no gain.
 *
 * Since VC of the Soldier's growth (D-087) the account is a bank the run never holds: every run
 * starts with no coins and no renown in hand (equal start, G12), its `RunStart` carries the
 * account as the run began, and every write of the save banks that start plus what the run
 * holds (`withAccount`), so a run left or dropped never loses the money and coins it spent are
 * gone. A run saved without its start banks nothing. The shapes did not change, so the
 * version stayed 6: the purse and renown fields mean the bank, as before.
 *
 * Since A69 the account counts the runs it has started: the ordinal is mixed into the salt of
 * a run's veins, so the second run of a day reads new land and a run is still named by its
 * seed text and its number.
 *
 * Since AP1 a signed-in account keeps its bank (purse, renown, runs) on the Worker too, and the
 * page writes the bank the Worker keeps into the save (`withBank`); the shape did not change.
 */
import { STORAGE_NAMESPACE } from "../config";
import { bankedAccount, type RunStart, type RunState } from "../core";

/** L1.1: where the platform's data store keeps the save; since AP1 the front page reads it too. */
export const SAVE_KEY = `${STORAGE_NAMESPACE}:save`;

export const SCHEMA_VERSION = 6;

export const MAX_SAVE_BYTES = 1024 * 1024;

interface SaveV1 {
  schemaVersion: 1;
  /** Milliseconds since epoch, supplied by the caller (core never reads clocks). */
  savedAt: number;
  /** Meta-progression placeholder. Grows in later increments. */
  unlocks: string[];
}

/** A run in progress (L1.1). */
export interface RunSave {
  /** The seed text the run was started from; it resumes only under the same one. */
  seedText: string;
  /** `CORE_VERSION` of the core that wrote the state. */
  coreVersion: number;
  state: RunState;
  /** What the run started with (K3); absent on a run saved before v5. */
  start?: RunStart;
}

interface SaveV2 {
  schemaVersion: 2;
  savedAt: number;
  unlocks: string[];
  /** The run to continue, or null when none is in progress. */
  run: RunSave | null;
}

interface SaveV3 {
  schemaVersion: 3;
  savedAt: number;
  unlocks: string[];
  run: RunSave | null;
  /** The account's purse (M2): the soldier's money, kept across runs. */
  purse: number;
}

interface SaveV4 {
  schemaVersion: 4;
  savedAt: number;
  unlocks: string[];
  run: RunSave | null;
  purse: number;
  /** The account's renown (C1): earned by deeds for others, kept across runs, never spent. */
  renown: number;
}

interface SaveV5 {
  schemaVersion: 5;
  savedAt: number;
  unlocks: string[];
  /** The run in progress, with what it started with (K3). */
  run: RunSave | null;
  purse: number;
  renown: number;
}

export interface SaveV6 {
  schemaVersion: 6;
  savedAt: number;
  unlocks: string[];
  run: RunSave | null;
  purse: number;
  renown: number;
  /** Runs the account has started (A69): the ordinal of the newest one. */
  runs: number;
}

export type Save = SaveV6;

export function createEmptySave(savedAt: number): Save {
  return {
    schemaVersion: SCHEMA_VERSION,
    savedAt,
    unlocks: [],
    run: null,
    purse: 0,
    renown: 0,
    runs: 0,
  };
}

/** A count the account keeps: a whole number, never under 0; anything else is 0. */
function soundCount(value: unknown): number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0 ? value : 0;
}

/** The run as this build writes it: a start that is not sound is dropped and the run stays. */
function soundRun(run: RunSave): RunSave {
  if (!("start" in run) || isRunStart(run.start)) {
    return run;
  }
  const { start: _unsound, ...rest } = run;
  return rest;
}

/** A save of any version before this build's. */
type OlderSave = SaveV1 | SaveV2 | SaveV3 | SaveV4 | SaveV5;

/**
 * An older save on a fresh one: its unlocks, and each field from the version that added it on
 * (the run from v2, its start checked from v4, the purse from v3, the renown from v4); what it
 * predates keeps the fresh value, and nothing else of it comes over.
 */
function lifted(old: OlderSave): Save {
  const version = old.schemaVersion;
  const fields: Partial<Omit<SaveV5, "schemaVersion">> = old;
  const kept = version >= 2 && isRunSave(fields.run) ? fields.run : null;
  const run = kept !== null && version >= 4 ? soundRun(kept) : kept;
  return {
    ...createEmptySave(old.savedAt),
    unlocks: old.unlocks,
    run,
    purse: version >= 3 ? soundCount(fields.purse) : 0,
    renown: version >= 4 ? soundCount(fields.renown) : 0,
  };
}

/**
 * Migrates any older persisted shape to the current one. v1 gains an empty run, v2 an empty
 * purse, v3 no renown, v4 a run without its start and v5 no runs counted; a run that is not
 * the shape this build writes is dropped and the unlocks, the purse and the renown stay.
 * Anything else is rejected loudly rather than guessed.
 */
export function migrate(raw: unknown): Save {
  if (!isRecord(raw) || typeof raw.schemaVersion !== "number") {
    throw new Error("save: not a versioned save object");
  }
  switch (raw.schemaVersion) {
    case 1:
    case 2:
    case 3:
    case 4:
    case 5:
      return lifted(raw as unknown as OlderSave);
    case 6: {
      const v6 = raw as unknown as SaveV6;
      const run = v6.run !== null && isRunSave(v6.run) ? soundRun(v6.run) : null;
      const purse = soundCount(v6.purse);
      const renown = soundCount(v6.renown);
      const runs = soundCount(v6.runs);
      return run === v6.run && purse === v6.purse && renown === v6.renown && runs === v6.runs
        ? v6
        : { ...v6, run, purse, renown, runs };
    }
    default:
      throw new Error(`save: unsupported schemaVersion ${raw.schemaVersion}`);
  }
}

/** The save with this run in it (null clears it) and the caller's timestamp. */
export function withRun(save: Save, run: RunSave | null, savedAt: number): Save {
  return { ...save, savedAt, run };
}

/**
 * The save with the account banked as the run in progress stands (VC): the account the run
 * started from plus the coins and renown it holds now (`bankedAccount`). The start is fixed for
 * the run, so every write of one run banks the same for the same state, and nothing is counted
 * twice. What the run has none of is left alone, and so is what the King owes (the run's, not
 * the account's); a run with no start (saved before v5) cannot say what is its own and leaves the
 * bank as it is. The same save comes back when nothing changes.
 */
export function withAccount(save: Save, state: RunState, start: RunStart | undefined): Save {
  const bank = start && bankedAccount(start, state);
  if (start === undefined || bank === undefined) {
    return save;
  }
  const purse = bank.purse;
  // A seat without renown (finds without the King's pay) has no renown of the run to bank.
  const renown = bank.renown ?? save.renown;
  return purse === save.purse && renown === save.renown ? save : { ...save, purse, renown };
}

/** The account's bank (AP1): the three counts a signed-in account keeps on the Worker. */
export interface Bank {
  readonly purse: number;
  readonly renown: number;
  readonly runs: number;
}

export function bankOf(save: Save): Bank {
  return { purse: save.purse, renown: save.renown, runs: save.runs };
}

/**
 * The save with the account's bank as the Worker keeps it (AP1). A saved run's start moves by the
 * same amounts, never under 0, so its next write (the start plus what the run holds, VC) banks
 * from the new bank, not the old one.
 */
export function withBank(save: Save, bank: Bank): Save {
  const start = save.run?.start;
  const moved = (value: number | undefined, by: number): number | undefined =>
    value === undefined ? undefined : Math.max(0, value + by);
  const run =
    save.run && start
      ? {
          ...save.run,
          start: {
            ...start,
            ...optional("purse", moved(start.purse, bank.purse - save.purse)),
            ...optional("renown", moved(start.renown, bank.renown - save.renown)),
          },
        }
      : save.run;
  return { ...save, run, purse: bank.purse, renown: bank.renown, runs: bank.runs };
}

/** A field to spread, or nothing for an undefined value. */
function optional<Key extends string>(
  key: Key,
  value: number | undefined,
): Partial<Record<Key, number>> {
  return value === undefined ? {} : ({ [key]: value } as Record<Key, number>);
}

/** The save with one more run started (A69): the new count is that run's ordinal. */
export function withRunStarted(save: Save): Save {
  return { ...save, runs: save.runs + 1 };
}

/** The saved run the game may continue: the same seed text, the same core, still running. */
export function resumableRun(
  save: Save,
  seedText: string,
  coreVersion: number,
): RunState | undefined {
  const run = save.run;
  if (
    run === null ||
    run.seedText !== seedText ||
    run.coreVersion !== coreVersion ||
    run.state.status !== "running"
  ) {
    return undefined;
  }
  return run.state;
}

export function serialize(save: Save): string {
  const json = JSON.stringify(save);
  const bytes = utf8Length(json);
  if (bytes > MAX_SAVE_BYTES) {
    throw new Error(`save: ${bytes} bytes exceeds MAX_SAVE_BYTES=${MAX_SAVE_BYTES}`);
  }
  return json;
}

export function deserialize(json: string): Save {
  return migrate(JSON.parse(json));
}

function isRunSave(value: unknown): value is RunSave {
  return (
    isRecord(value) &&
    typeof value.seedText === "string" &&
    typeof value.coreVersion === "number" &&
    isRecord(value.state) &&
    typeof value.state.tick === "number" &&
    typeof value.state.status === "string" &&
    Array.isArray(value.state.hexes)
  );
}

function isRunStart(value: unknown): value is RunStart {
  const isCount = (count: unknown): boolean => soundCount(count) === count;
  return (
    isRecord(value) &&
    isCount(value.land) &&
    isCount(value.cities) &&
    (value.purse === undefined || isCount(value.purse)) &&
    (value.renown === undefined || isCount(value.renown))
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function utf8Length(text: string): number {
  return new TextEncoder().encode(text).length;
}
